/* Shared wire contract for the driver demand heat map ("Hot zones").
 *
 * This file exists TWICE, byte-identical:
 *   common/src/other/demandHeatCodec.js   (mobile + web, via Metro / Webpack)
 *   functions/common/demandCodec.js        (Cloud Functions — the functions
 *                                           directory is deployed on its own,
 *                                           so it cannot require ../../common)
 * functions/test/demandHeat.spec.js asserts the two copies agree. Edit both.
 *
 * Snapshot at /demand_heat (written by publishDemandHeat every 10 min):
 *   s  summary — read on its own by the ambient chip (~0.75 KB)
 *      { v: 1, at: ms, off: bool, q: 0|1, slot: 0..167 (dow*24+HH, Addis),
 *        a: live share alpha, ref: requests/hour that equals level 9,
 *        g: [lat0, lng0, step], n: [zone, ...] (Now, <= 5), x: [zone, ...] (Next hour, <= 5) }
 *      zone = [lat, lng, lv 1..9, tr 0|1|2 (below|usual|above usual),
 *              drv 0..9 (online drivers in the zone), cells, en|null, am|null]
 *   f  full cell strings — read only while the sheet / Demand view is open
 *      { at: ms, n: "<cells>", x: "<cells>" }
 *   _meta  ops counters, never read by drivers
 *
 * City events (phase 2, CITY_EVENTS.md) — present only while
 * settings.cityEventsDriverEnabled is on:
 *   s.ev  [event, ...] (<= MAX_EVENTS, ordered by the next crowd wave)
 *      event = [id, lat, lng, start, end, size 1..3, cat, en, am, venueEn, venueAm,
 *               [arrivalBefore, arrivalAfter, arrivalWeight, exitBefore, exitAfter, exitWeight]]
 *      minutes around start (arrival wave) / end (exit wave), weight 0..1
 *   a zone tuple may carry a 9th element: the id of the event whose EXPECTED
 *      crowd produced that Next-hour zone (clients say "Expected", not a trend)
 *
 * Cells: fixed 5 characters each, no separators, hottest first:
 *   rr = row in base-36 (2 chars), cc = col in base-36 (2 chars), l = level '1'..'9'
 *   centre lat = g[0] + (row + 0.5) * g[2]; centre lng = g[1] + (col + 0.5) * g[2]
 * Counters at /demand_cells (server only) use the key r{row}c{col}.
 */
'use strict';

var CODEC_VERSION = 1;
var MAX_EVENTS = 8;
var EVENT_TITLE_MAX = 40;
var EVENT_VENUE_MAX = 28;
var MINUTE_MS = 60 * 1000;

// ~500 m square cells over Addis Ababa. 0.0045 deg = 497.6 m N-S, 494.8 m E-W at 9 N.
// Bounds mirror DEFAULT_ADDIS_BBOX (functions/common/osmImport.js). RTDB keys
// reject '.', so cells are addressed by integer row/col, never by lat/lng.
var DEFAULT_GRID = { lat0: 8.85, lng0: 38.55, lat1: 9.15, lng1: 39.0, step: 0.0045 };
var B36 = '0123456789abcdefghijklmnopqrstuvwxyz';

function isNum(x) { return typeof x === 'number' && isFinite(x); }
function round5(x) { return Math.round(x * 1e5) / 1e5; }

// Grid from the snapshot header `g` (so the client never hardcodes geometry).
function gridFrom(g) {
  if (Array.isArray(g) && g.length >= 3 && isNum(Number(g[0])) && isNum(Number(g[1])) && Number(g[2]) > 0) {
    return { lat0: Number(g[0]), lng0: Number(g[1]), step: Number(g[2]), lat1: DEFAULT_GRID.lat1, lng1: DEFAULT_GRID.lng1 };
  }
  return DEFAULT_GRID;
}

function cellOf(lat, lng, grid) {
  var g = grid || DEFAULT_GRID;
  if (!isNum(lat) || !isNum(lng)) return null;
  if (lat < g.lat0 || lat >= g.lat1 || lng < g.lng0 || lng >= g.lng1) return null;
  var row = Math.floor((lat - g.lat0) / g.step);
  var col = Math.floor((lng - g.lng0) / g.step);
  return { row: row, col: col, key: 'r' + row + 'c' + col };
}

function parseKey(key) {
  var m = /^r(\d+)c(\d+)$/.exec(String(key));
  return m ? { row: parseInt(m[1], 10), col: parseInt(m[2], 10) } : null;
}

function cellCentre(row, col, grid) {
  var g = grid || DEFAULT_GRID;
  return { lat: round5(g.lat0 + (row + 0.5) * g.step), lng: round5(g.lng0 + (col + 0.5) * g.step) };
}

function b36(n) {
  n = Math.max(0, Math.min(1295, Math.floor(n)));
  return B36.charAt(Math.floor(n / 36)) + B36.charAt(n % 36);
}
function unb36(s) {
  var a = B36.indexOf(s.charAt(0)), b = B36.indexOf(s.charAt(1));
  return (a < 0 || b < 0) ? -1 : a * 36 + b;
}

// cells: [{row, col, lv}] -> fixed-width string, level 9 first (a client slice keeps the hottest).
function encodeCells(cells) {
  var list = [];
  (cells || []).forEach((c) => {
    if (!c || !isNum(c.row) || !isNum(c.col) || !isNum(c.lv)) return;
    var lv = Math.min(9, Math.max(1, Math.round(c.lv)));
    if (c.lv < 0.5) return;
    list.push({ row: c.row, col: c.col, lv: lv });
  });
  list.sort((p, q) => (q.lv - p.lv) || (p.row - q.row) || (p.col - q.col));
  return list.map((c) => b36(c.row) + b36(c.col) + String(c.lv)).join('');
}

// string -> [{row, col, lv, latitude, longitude, weight 0..1}], malformed segments skipped.
function decodeCells(str, g) {
  var grid = gridFrom(g);
  var out = [];
  if (typeof str !== 'string') return out;
  for (var i = 0; i + 5 <= str.length; i += 5) {
    var row = unb36(str.substr(i, 2));
    var col = unb36(str.substr(i + 2, 2));
    var lv = parseInt(str.charAt(i + 4), 10);
    if (row < 0 || col < 0 || !(lv >= 1 && lv <= 9)) continue;
    var c = cellCentre(row, col, grid);
    out.push({ row: row, col: col, lv: lv, latitude: c.lat, longitude: c.lng, weight: lv / 9 });
  }
  return out;
}

// Level words: 1-3 Warming, 4-6 Busy, 7-9 Very busy.
function levelWord(lv) { return lv >= 7 ? 'very' : (lv >= 4 ? 'busy' : 'warming'); }

// [lat, lng, lv, tr, drv, cells, en, am, ev?] -> object, or null when malformed.
function zoneFromTuple(t) {
  if (!Array.isArray(t) || t.length < 6) return null;
  var lat = Number(t[0]), lng = Number(t[1]), lv = Number(t[2]);
  if (!isNum(lat) || !isNum(lng) || !isNum(lv)) return null;
  var tr = Number(t[3]);
  return {
    lat: lat,
    lng: lng,
    lv: Math.min(9, Math.max(1, Math.round(lv))),
    trend: tr === 2 ? 1 : (tr === 0 ? -1 : 0),
    drivers: Math.max(0, Math.min(9, Math.round(Number(t[4]) || 0))),
    cells: Math.max(1, Math.round(Number(t[5]) || 1)),
    en: typeof t[6] === 'string' && t[6] ? t[6] : null,
    am: typeof t[7] === 'string' && t[7] ? t[7] : null,
    ev: typeof t[8] === 'string' && t[8] ? t[8] : null,
  };
}

// {lat, lng, lv, trend -1|0|1, drivers, cells, en, am, ev?} -> tuple for the snapshot.
function zoneToTuple(z) {
  var t = [round5(z.lat), round5(z.lng), z.lv, z.trend > 0 ? 2 : (z.trend < 0 ? 0 : 1),
    Math.max(0, Math.min(9, Math.round(z.drivers || 0))), Math.max(1, Math.round(z.cells || 1)), z.en || null, z.am || null];
  if (typeof z.ev === 'string' && z.ev) t.push(z.ev);
  return t;
}

// ── City events ─────────────────────────────────────────────────────────

function text(v, max) {
  if (typeof v !== 'string') return null;
  var s = v.replace(/\s+/g, ' ').trim();
  if (!s) return null;
  return s.length > max ? s.slice(0, max - 1).trim() + '…' : s;
}
function minutes(v, def) {
  var n = Number(v);
  return isNum(n) && n >= 0 ? Math.min(24 * 60, Math.round(n)) : def;
}
function weight(v, def) {
  var n = Number(v);
  return isNum(n) ? Math.min(1, Math.max(0, n)) : def;
}
function sizeOf(v) {
  var n = Math.round(Number(v));
  return isNum(n) ? Math.min(3, Math.max(1, n)) : 1;
}

// Demand windows with defaults: { arrival: {before, after, weight}, exit: {...} }.
function eventWindows(w) {
  var a = (w && w.arrival) || {};
  var x = (w && w.exit) || {};
  return {
    arrival: { before: minutes(a.before, 0), after: minutes(a.after, 0), weight: weight(a.weight, 0) },
    exit: { before: minutes(x.before, 0), after: minutes(x.after, 0), weight: weight(x.weight, 0) },
  };
}

// {id, lat, lng, start, end, size, cat, en, am, venue, venueAm, arrival, exit} -> tuple.
function eventToTuple(ev) {
  var w = eventWindows(ev);
  return [String(ev.id), round5(Number(ev.lat)), round5(Number(ev.lng)), Math.round(Number(ev.start)), Math.round(Number(ev.end)),
    sizeOf(ev.size), text(ev.cat, 16) || 'other', text(ev.en, EVENT_TITLE_MAX), text(ev.am, EVENT_TITLE_MAX),
    text(ev.venue, EVENT_VENUE_MAX), text(ev.venueAm, EVENT_VENUE_MAX),
    [w.arrival.before, w.arrival.after, w.arrival.weight, w.exit.before, w.exit.after, w.exit.weight]];
}

// tuple -> event object, or null when malformed (so one bad entry never hides the rest).
function eventFromTuple(t) {
  if (!Array.isArray(t) || t.length < 12) return null;
  var id = typeof t[0] === 'string' ? t[0] : String(t[0] || '');
  var lat = Number(t[1]), lng = Number(t[2]), start = Number(t[3]), end = Number(t[4]);
  if (!id || !isNum(lat) || !isNum(lng) || !isNum(start) || !isNum(end) || !(end > start)) return null;
  var w = Array.isArray(t[11]) ? t[11] : [];
  return {
    id: id,
    lat: lat,
    lng: lng,
    start: start,
    end: end,
    size: sizeOf(t[5]),
    cat: typeof t[6] === 'string' && t[6] ? t[6] : 'other',
    en: typeof t[7] === 'string' && t[7] ? t[7] : null,
    am: typeof t[8] === 'string' && t[8] ? t[8] : null,
    venue: typeof t[9] === 'string' && t[9] ? t[9] : null,
    venueAm: typeof t[10] === 'string' && t[10] ? t[10] : null,
    arrival: { before: minutes(w[0], 0), after: minutes(w[1], 0), weight: weight(w[2], 0) },
    exit: { before: minutes(w[3], 0), after: minutes(w[4], 0), weight: weight(w[5], 0) },
  };
}

// The two crowd waves of an event, ms: arrival [start - before, start + after],
// exit [end - before, end + after]. A wave with weight 0 still has bounds (so
// phases stay monotonic) but carries no demand prior.
function eventWaves(ev) {
  var w = eventWindows(ev);
  var start = Number(ev.start), end = Number(ev.end);
  return {
    arrivalFrom: start - w.arrival.before * MINUTE_MS,
    arrivalTo: start + w.arrival.after * MINUTE_MS,
    exitFrom: end - w.exit.before * MINUTE_MS,
    exitTo: end + w.exit.after * MINUTE_MS,
  };
}

// 'upcoming' | 'arriving' | 'ongoing' | 'leaving' | 'over' at `now`.
function eventPhase(ev, now) {
  var t = Number(now);
  var v = eventWaves(ev);
  if (t > v.exitTo) return 'over';
  if (t >= v.exitFrom) return 'leaving';
  if (t >= Math.max(v.arrivalTo, Number(ev.start))) return 'ongoing';
  if (t >= v.arrivalFrom) return 'arriving';
  return 'upcoming';
}

// When the next wave that matters to a driver starts (ms): the arrival wave
// while it is ahead and weighted, else the exit wave; null once the event is
// over. Current waves return their (past) start, so sorting by this value puts
// "happening now" first and "later tonight" after it.
function eventNextWaveAt(ev, now) {
  var phase = eventPhase(ev, now);
  var v = eventWaves(ev);
  var w = eventWindows(ev);
  if (phase === 'over') return null;
  if (phase === 'upcoming' || phase === 'arriving') return w.arrival.weight > 0 ? v.arrivalFrom : v.exitFrom;
  return v.exitFrom;
}

function isUsableSummary(s) {
  return Boolean(s && s.v === CODEC_VERSION && isNum(s.at) && Array.isArray(s.g));
}

module.exports = {
  CODEC_VERSION: CODEC_VERSION,
  DEFAULT_GRID: DEFAULT_GRID,
  gridFrom: gridFrom,
  cellOf: cellOf,
  parseKey: parseKey,
  cellCentre: cellCentre,
  encodeCells: encodeCells,
  decodeCells: decodeCells,
  levelWord: levelWord,
  zoneFromTuple: zoneFromTuple,
  zoneToTuple: zoneToTuple,
  isUsableSummary: isUsableSummary,
  MAX_EVENTS: MAX_EVENTS,
  EVENT_TITLE_MAX: EVENT_TITLE_MAX,
  EVENT_VENUE_MAX: EVENT_VENUE_MAX,
  eventWindows: eventWindows,
  eventToTuple: eventToTuple,
  eventFromTuple: eventFromTuple,
  eventWaves: eventWaves,
  eventPhase: eventPhase,
  eventNextWaveAt: eventNextWaveAt,
};
